import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'
import { useSession } from '@/auth/session'
import { Button } from '@/components/ui/button'
import { formatInstant } from '@/format/date'
import { acknowledgeClearance, fetchLastReceivedAt } from '@/outbox/clearance'
import type { ClearanceStatus } from '@/outbox/clearance'
import { landingFor } from './landing'

/**
 * Task 18's "Lost" scaffold, filled (`shell/SyncTray.tsx`'s own top comment
 * named this task as the one that would supply the real data source).
 * Renders NOTHING unless `status` is `'cleared'` — the same "each section
 * renders nothing when empty" rule its two siblings ("Waiting to send",
 * "Needs your attention") already follow, so this component IS the section,
 * not a wrapper around one `SyncTray.tsx` still has to gate.
 *
 * `status` is a PROP, not this component's own `useClearanceStatus()` call —
 * CORRECTED, review round 1's Important 1: `SyncTray.tsx` now calls that
 * hook itself (see its own comment for why detection must run regardless of
 * whether the sheet is open, which this component — rendered inside
 * `<SheetContent>`, a Radix portal with no `forceMount` — cannot achieve on
 * its own) and threads the result down, rather than this component
 * subscribing a second, redundant time.
 *
 * NEVER RENDERS A COUNT. `clearance.ts`'s own top comment has the full
 * reasoning (architecture §8 asks for one; §0.6 shows it cannot be built);
 * this component's only numeric-feeling content is a TIMESTAMP, fetched
 * fresh from the one place that can honestly answer it
 * (`fetchLastReceivedAt`, `GET /api/last-received`).
 *
 * `useQuery`, not a bespoke `useEffect`/`useState` pair — CLAUDE.md:
 * "react-query is the only server cache". `enabled: status === 'cleared'`
 * means this never even ISSUES the request on every other boot (the
 * overwhelmingly common case), matching `StorageCleared`'s own "present
 * and empty" discipline one level deeper: no network call, not just no
 * visible content, when there is nothing to show.
 */
export function StorageCleared({ status }: { readonly status: ClearanceStatus | 'checking' }) {
  const { me } = useSession()

  const {
    data: lastReceivedAt,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['clearance', 'last-received'],
    queryFn: fetchLastReceivedAt,
    enabled: status === 'cleared',
    // Fixed once known for this boot: a `'cleared'` boot's boundary instant
    // does not change while the tab stays open, and re-fetching on window
    // focus would cost a request for a figure that can only ever answer
    // the SAME instant again. Only governs the SUCCESS case — react-query's
    // own default retry still runs on failure before `isError` below ever
    // becomes true. `api/queryClient.ts` reproduces that default count
    // exactly for every error except `ResultMismatchError`, and
    // `fetchLastReceivedAt` throws `ApiError`, never that — so this path
    // keeps all four attempts.
    staleTime: Infinity,
  })

  if (status !== 'cleared') return null

  // THREE distinct honest states, not two — CORRECTED, review round 1's
  // Important 2: an earlier version of `fetchLastReceivedAt` swallowed a
  // network failure into the SAME `null` a genuine "no events recorded"
  // answer produces, so an unreachable server (exactly the likely state of
  // a device that just lost its storage) rendered as a confident claim that
  // nothing was ever recorded — a definite negative asserted from not
  // knowing. `isError` is now a real, separate branch: `fetchLastReceivedAt`
  // throws on any failure instead of returning `null` for one, so a `null`
  // reaching here is ONLY ever the server's own genuine answer.
  const boundary = isError
    ? "GridFit could not be reached to check what it last received from your account. Try again once you're back online."
    : isLoading
      ? 'Checking what GridFit last received from your account…'
      : lastReceivedAt
        ? // "your account", not "this device" — CORRECTED, review round 1's
          // CRITICAL: `principal.id` (what the server actually scoped this
          // answer to) is an ACCOUNT identity, identical across every device
          // that account signs into, not a per-device one. "Anything this
          // device sent after that — and anything it never managed to
          // send" names both ways THIS device's own work can be missing
          // from that instant, without pretending to distinguish them (the
          // server cannot either) — see `clearance.ts`'s own top comment
          // for the worked example this replaces.
          `GridFit last received work from your account at ${formatInstant(lastReceivedAt)}. Anything this device sent after that — and anything it never managed to send — was lost.`
        : 'GridFit has no work recorded from your account yet. If you entered anything on this device before this happened, it was lost.'

  return (
    <section aria-label="Lost" className="space-y-2">
      <h3 className="text-sm font-medium text-muted-foreground">Lost</h3>
      <p className="text-[length:var(--gf-text)]">This device&rsquo;s stored data was cleared. {boundary}</p>
      <div className="flex flex-wrap items-center gap-3">
        <Link to={landingFor(me)} className="text-[length:var(--gf-text)] underline">
          Review what GridFit has for you
        </Link>
        <Button type="button" variant="outline" size="sm" className="min-h-[var(--gf-target)]" onClick={acknowledgeClearance}>
          Got it
        </Button>
      </div>
    </section>
  )
}
